// Common timezone options for user selection
export const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC+0 - Coordinated Universal Time' },
  { value: 'Pacific/Honolulu', label: 'UTC-10 - Hawaii (Honolulu)' },
  { value: 'America/Anchorage', label: 'UTC-9 - Alaska (Anchorage)' },
  { value: 'America/Los_Angeles', label: 'UTC-8 - Pacific Time (Los Angeles, San Francisco)' },
  { value: 'America/Denver', label: 'UTC-7 - Mountain Time (Denver, Phoenix)' },
  { value: 'America/Chicago', label: 'UTC-6 - Central Time (Chicago, Dallas)' },
  { value: 'America/New_York', label: 'UTC-5 - Eastern Time (New York, Miami)' },
  { value: 'America/Santiago', label: 'UTC-4 - Chile (Santiago)' },
  { value: 'America/Sao_Paulo', label: 'UTC-3 - Brazil (São Paulo, Rio de Janeiro)' },
  { value: 'America/Noronha', label: 'UTC-2 - Fernando de Noronha' },
  { value: 'Atlantic/Azores', label: 'UTC-1 - Azores' },
  { value: 'Europe/London', label: 'UTC+0 - London, Dublin, Edinburgh' },
  { value: 'Europe/Paris', label: 'UTC+1 - Paris, Berlin, Rome, Madrid' },
  { value: 'Europe/Helsinki', label: 'UTC+2 - Helsinki, Cairo, Athens' },
  { value: 'Europe/Moscow', label: 'UTC+3 - Moscow, Istanbul' },
  { value: 'Asia/Dubai', label: 'UTC+4 - Dubai, Abu Dhabi' },
  { value: 'Asia/Kolkata', label: 'UTC+5:30 - Mumbai, Delhi, Kolkata' },
  { value: 'Asia/Dhaka', label: 'UTC+6 - Dhaka, Almaty' },
  { value: 'Asia/Bangkok', label: 'UTC+7 - Bangkok, Jakarta' },
  { value: 'Asia/Shanghai', label: 'UTC+8 - Beijing, Shanghai, Singapore' },
  { value: 'Asia/Tokyo', label: 'UTC+9 - Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: 'UTC+10 - Sydney, Melbourne' },
  { value: 'Pacific/Auckland', label: 'UTC+12 - Auckland, Wellington' },
];

// Helper function to format date/time in user's timezone
export function formatDateInTimezone(
  date: Date | string,
  timezone: string = 'UTC',
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    ...options,
  };

  return new Intl.DateTimeFormat('en-US', {
    ...defaultOptions,
    timeZone: timezone,
  }).format(dateObj);
}

// Helper function to format date only (no time, no timezone conversion)
export function formatDateOnlyInTimezone(
  date: Date | string,
  timezone: string = 'UTC'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Format date only without time - use UTC to avoid timezone shifts
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC', // Always use UTC to prevent date shifting
  }).format(dateObj);
}

// Helper function to format time only in user's timezone
export function formatTimeOnlyInTimezone(
  date: Date | string,
  timezone: string = 'UTC'
): string {
  return formatDateInTimezone(date, timezone, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// Helper function to get timezone abbreviation
export function getTimezoneAbbreviation(timezone: string): string {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  });
  
  const parts = formatter.formatToParts(now);
  const timeZonePart = parts.find(part => part.type === 'timeZoneName');
  return timeZonePart?.value || timezone;
}

// Helper function to validate timezone with fallback to UTC
export function getSafeTimezone(timezone: string | undefined): string {
  if (!timezone) return 'UTC';
  
  try {
    // Validate timezone by attempting to use it
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return timezone;
  } catch (error) {
    console.warn(`Invalid timezone: ${timezone}, falling back to UTC`);
    return 'UTC';
  }
}

// Helper function for safe date formatting with error handling
export function formatDateSafely(
  date: Date | string | null | undefined,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    const safeTimezone = getSafeTimezone(timezone);
    return formatDateInTimezone(dateObj, safeTimezone, options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error displaying date';
  }
}

// Convert a date from user's timezone to UTC for API submission
export function convertToUTC(
  date: Date | string,
  fromTimezone: string
): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Get the date components in the source timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: fromTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  const parts = formatter.formatToParts(dateObj);
  const getValue = (type: string) => parts.find(p => p.type === type)?.value || '0';
  
  // Create a date string in the source timezone
  const year = getValue('year');
  const month = getValue('month');
  const day = getValue('day');
  const hour = getValue('hour');
  const minute = getValue('minute');
  const second = getValue('second');
  
  // Parse as UTC (this gives us the "wall clock" time in the source timezone)
  const utcDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  
  // Calculate the offset between the source timezone and UTC at this date
  const sourceDate = new Date(dateObj.toLocaleString('en-US', { timeZone: fromTimezone }));
  const utcReference = new Date(dateObj.toLocaleString('en-US', { timeZone: 'UTC' }));
  const offset = utcReference.getTime() - sourceDate.getTime();
  
  // Apply the offset to get the true UTC time
  return new Date(utcDate.getTime() + offset);
}

// Convert a UTC date to user's timezone
export function convertToUserTimezone(
  utcDate: Date | string,
  toTimezone: string
): Date {
  const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  // Get the date/time in the target timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: toTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  const parts = formatter.formatToParts(dateObj);
  const getValue = (type: string) => parts.find(p => p.type === type)?.value || '0';
  
  const year = getValue('year');
  const month = getValue('month');
  const day = getValue('day');
  const hour = getValue('hour');
  const minute = getValue('minute');
  const second = getValue('second');
  
  // Create a new Date object with the timezone-adjusted values
  // Note: This creates a Date object that represents the same moment in time,
  // but when displayed in the local system timezone, it will show the target timezone's values
  return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
}

// Parse a date string in user's timezone
export function parseDateInTimezone(
  dateString: string,
  timezone: string
): Date {
  // If the date string already has timezone info, parse it directly
  const date = new Date(dateString);
  
  // If it's a valid date, convert it assuming it's in the user's timezone
  if (!isNaN(date.getTime())) {
    // If the string doesn't contain timezone info (no 'Z' or offset)
    if (!dateString.includes('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      // Treat it as a date in the user's timezone
      return convertToUTC(date, timezone);
    }
    return date;
  }
  
  throw new Error(`Invalid date string: ${dateString}`);
}

// Get current date/time in user's timezone
export function nowInTimezone(timezone: string): Date {
  const now = new Date();
  return convertToUserTimezone(now, timezone);
}

// Format relative time (e.g., "2 hours ago") in user's timezone
export function formatRelativeTime(
  date: Date | string,
  timezone: string
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  
  // Calculate difference in milliseconds
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  
  // Future dates
  if (diffMs < 0) {
    const absDiffSeconds = Math.abs(diffSeconds);
    const absDiffMinutes = Math.abs(diffMinutes);
    const absDiffHours = Math.abs(diffHours);
    const absDiffDays = Math.abs(diffDays);
    const absDiffWeeks = Math.abs(diffWeeks);
    const absDiffMonths = Math.abs(diffMonths);
    const absDiffYears = Math.abs(diffYears);
    
    if (absDiffSeconds < 60) return 'in a few seconds';
    if (absDiffMinutes < 60) return `in ${absDiffMinutes} ${absDiffMinutes === 1 ? 'minute' : 'minutes'}`;
    if (absDiffHours < 24) return `in ${absDiffHours} ${absDiffHours === 1 ? 'hour' : 'hours'}`;
    if (absDiffDays < 7) return `in ${absDiffDays} ${absDiffDays === 1 ? 'day' : 'days'}`;
    if (absDiffWeeks < 4) return `in ${absDiffWeeks} ${absDiffWeeks === 1 ? 'week' : 'weeks'}`;
    if (absDiffMonths < 12) return `in ${absDiffMonths} ${absDiffMonths === 1 ? 'month' : 'months'}`;
    return `in ${absDiffYears} ${absDiffYears === 1 ? 'year' : 'years'}`;
  }
  
  // Past dates
  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  if (diffWeeks < 4) return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
  if (diffMonths < 12) return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
  return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
}